import { BrowserModule } from "@angular/platform-browser";
import { CommonModule } from "@angular/common";
import { NgModule, LOCALE_ID, APP_INITIALIZER } from "@angular/core";
import { HttpClientModule, HTTP_INTERCEPTORS } from "@angular/common/http";
import { ReactiveFormsModule, FormsModule } from "@angular/forms";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { registerLocaleData } from "@angular/common";
import localeEl from "@angular/common/locales/el";

// Angular Material Imports (Διορθωμένα sub-paths για v17)
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatTabsModule } from '@angular/material/tabs';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatListModule } from '@angular/material/list';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBarModule } from "@angular/material/snack-bar";
import { MatDialogModule } from "@angular/material/dialog";

// Τρίτες βιβλιοθήκες
import { NgbModule, NgbDateParserFormatter } from "@ng-bootstrap/ng-bootstrap";
import { NgSelectModule } from "@ng-select/ng-select";
import { EditorModule } from "@tinymce/tinymce-angular";
import { KeycloakAngularModule, KeycloakService } from 'keycloak-angular';

// Δικά σου Components/Modules/Services
import { AppRoutingModule } from "./app-routing.module";
import { AppComponent } from "./app.component";
import { HomeComponent } from "./home/home.component";
import { EmployersComponent } from "./employers/employers.component";
import { EmployersModule } from "./employers/employers.module";
import { HeaderComponent } from "./header/header.component";
import { JobListingComponent } from "./job-listing/job-listing.component";
import { JobDetailsComponent } from "./job-details/job-details.component";
import { SharedModule } from "./shared/shared.module";
import { ProfileResolver } from "./employer-profile/employer-profile.resolver";
import { ProfileComponent } from "./employer-profile/employer-profile.component";
import { CandidateProfileComponent } from "./candidate-profile/candidate-profile.component";
import { CandidatesListComponent } from "./candidates-list/candidates-list.component";
import { ConstantsService } from "./ConstantsService";
import { SafePipe } from "./safe.pipe";
import { PrivacyPolicyComponent } from "./privacy-policy/privacy-policy.component";
import { FaqComponent } from "./faq/faq.component";
import { AdministratorCvHandlingComponent } from "./administrator-cv-handling/administrator-cv-handling.component";
import { AlertMessagesModule } from "./messages-helpers/messages-helpers.module";
import { LoadingSpinnerComponent } from "./loading-spinner/loading-spinner.component";
import { AdministratorHeaderComponent } from "./administrator-header/administrator-header.component";
import { AdministratorFooterComponent } from "./administrator-footer/administrator-footer.component";
import { PaginationModule } from "./pagination/pagination.module";
import { PaginationBsModule } from "./pagination-bs/pagination-bs.module";
import { AdministratorCompanyHandlingComponent } from "./administrator-company-handling/administrator-company-handling.component";
import { AdministratorJobPostHandlingComponent } from "./administrator-job-post-handling/administrator-job-post-handling.component";
import { NgbDateCustomParserFormatter } from "./NgbDateCustomParserFormatter";
import { environment } from "src/environments/environment";
import { KeycloakBearerInterceptor } from './HttpInterceptors/keycloak-bearer.interceptor';
import { ReconnectDialogComponent } from './reconnect-dialog/reconnect-dialog.component';

registerLocaleData(localeEl, "el");

// Factory function για το Keycloak Initialization
function initializeKeycloak(keycloak: KeycloakService) {
  return () =>
    keycloak.init({
      config: {
        url: environment.keycloak.url,
        realm: environment.keycloak.realm,
        clientId: environment.keycloak.clientId,
      },
      initOptions: {
        onLoad: 'check-sso',
        silentCheckSsoRedirectUri: window.location.origin + '/assets/silent-check-sso.html',
        pkceMethod: 'S256',
        checkLoginIframe: false
      },
      enableBearerInterceptor: false, // Το γυρίζουμε σε true για να χρησιμοποιεί τον αυτόματο interceptor
      bearerExcludedUrls: ['/assets', '/clients/public']
    });
}

@NgModule({
  declarations: [
    AppComponent,
    HomeComponent,
    HeaderComponent,
    JobListingComponent,
    JobDetailsComponent,
    ProfileComponent,
    CandidateProfileComponent,
    CandidatesListComponent,
    SafePipe,
    PrivacyPolicyComponent,
    FaqComponent,
    AdministratorCvHandlingComponent,
    AdministratorHeaderComponent,
    AdministratorFooterComponent,
    LoadingSpinnerComponent,
    AdministratorJobPostHandlingComponent,
    AdministratorCompanyHandlingComponent,
    ReconnectDialogComponent,
  ],
  imports: [
    BrowserModule,
    CommonModule,
    BrowserAnimationsModule,
    HttpClientModule,
    FormsModule,
    ReactiveFormsModule,
    AppRoutingModule,
    NgbModule,
    NgSelectModule,
    EditorModule,
    KeycloakAngularModule,
    // Material Modules
    MatSidenavModule,
    MatTabsModule,
    MatNativeDateModule,
    MatDatepickerModule,
    MatIconModule,
    MatButtonModule,
    MatCheckboxModule,
    MatToolbarModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatRadioModule,
    MatListModule,
    MatSelectModule,
    MatSnackBarModule,
    MatDialogModule,
    // Custom Modules
    EmployersModule,
    SharedModule,
    AlertMessagesModule,
    PaginationModule,
    PaginationBsModule
  ],
  providers: [
    ConstantsService,
    ProfileResolver,
    { provide: LOCALE_ID, useValue: "el" },
    { provide: NgbDateParserFormatter, useClass: NgbDateCustomParserFormatter },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeKeycloak,
      multi: true,
      deps: [KeycloakService],
    },
    { provide: HTTP_INTERCEPTORS, useClass: KeycloakBearerInterceptor, multi: true }
  ],
  bootstrap: [AppComponent],
})
export class AppModule { }